import React, { useState } from 'react';
import { Box, Typography, Paper, Grid, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, TextField, MenuItem, Switch, FormControlLabel, Divider, Collapse } from '@mui/material';
import { WalletIcon, BellIcon, PlusCircleIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
const FloatBalancePage: React.FC = () => {
  const [showHistory, setShowHistory] = useState(false);
  const summaryData = {
    total: {
      value: '12,650,000',
      currency: 'NGN',
      label: 'TOTAL FLOAT'
    },
    reserved: {
      value: '1,850,000',
      currency: 'NGN',
      label: 'RESERVED'
    },
    available: {
      value: '10,800,000',
      currency: 'NGN',
      label: 'AVAILABLE'
    }
  };
  const currencyBalances = [{
    currency: 'NGN',
    name: 'Nigerian Naira',
    available: '8,500,000',
    reserved: '1,200,000',
    total: '9,700,000',
    threshold: '2,000,000',
    status: 'Healthy'
  }, {
    currency: 'GHS',
    name: 'Ghanaian Cedi',
    available: '45,200',
    reserved: '5,800',
    total: '51,000',
    threshold: '10,000',
    status: 'Healthy'
  }, {
    currency: 'KES',
    name: 'Kenyan Shilling',
    available: '620,000',
    reserved: '180,000',
    total: '800,000',
    threshold: '500,000',
    status: 'Warning'
  }, {
    currency: 'XOF',
    name: 'CFA Franc',
    available: '3,100,000',
    reserved: '900,000',
    total: '4,000,000',
    threshold: '5,000,000',
    status: 'Critical'
  }];
  const chartData = [{
    date: 'Day 1',
    balance: 9.5
  }, {
    date: 'Day 5',
    balance: 11.2
  }, {
    date: 'Day 10',
    balance: 10.1
  }, {
    date: 'Day 15',
    balance: 12.8
  }, {
    date: 'Day 20',
    balance: 11.5
  }, {
    date: 'Day 25',
    balance: 8.9
  }, {
    date: 'Day 30',
    balance: 10.8
  }];
  const previousTopUps = [{
    date: '2025-01-15',
    amount: '10,000,000 NGN',
    status: 'Completed'
  }, {
    date: '2025-01-02',
    amount: '500,000 KES',
    status: 'Completed'
  }, {
    date: '2024-12-20',
    amount: '2,000,000 XOF',
    status: 'Completed'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Healthy':
        return {
          bg: '#0B7923',
          text: '#FFFFFF'
        };
      case 'Warning':
        return {
          bg: '#B38600',
          text: '#FFFFFF'
        };
      case 'Critical':
        return {
          bg: '#AE2A19',
          text: '#FFFFFF'
        };
      default:
        return {
          bg: '#9CA3AF',
          text: '#FFFFFF'
        };
    }
  };
  return <Box sx={{
    p: {
      xs: 2,
      md: 4
    },
    bgcolor: '#F8F9FA',
    minHeight: '100vh'
  }} data-id="element-1187">
      <Typography variant="h5" sx={{
      fontWeight: 700,
      color: '#1F2937',
      mb: 4,
      fontSize: '24px'
    }} data-id="element-1188">
        Float & Balance
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{
      mb: 4
    }} data-id="element-1189">
        {[summaryData.total, summaryData.reserved, summaryData.available].map((card, index) => <Grid item xs={12} md={4} key={index} data-id="element-1190">
              <Card elevation={0} sx={{
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
        }} data-id="element-1191">
                <CardContent sx={{
            p: 3
          }} data-id="element-1192">
                  <Typography sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#6B7280',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              mb: 2
            }} data-id="element-1193">
                    {card.label}
                  </Typography>
                  <Typography sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: index === 2 ? '#0B7923' : index === 1 ? '#B38600' : '#1F2937',
              lineHeight: 1,
              mb: 1
            }} data-id="element-1194">
                    {card.value}
                  </Typography>
                  <Typography sx={{
              fontSize: '12px',
              color: '#9CA3AF',
              fontWeight: 500
            }} data-id="element-1195">
                    {card.currency}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>)}
      </Grid>

      <Grid container spacing={4} data-id="element-1196">
        {/* Full Width Column for Table and Chart */}
        <Grid item xs={12} data-id="element-1197">
          <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4
        }} data-id="element-1198">
            {/* Currency Balance Table */}
            <Paper elevation={0} sx={{
            borderRadius: 2,
            border: '1px solid #E5E7EB',
            overflow: 'hidden'
          }} data-id="element-1199">
              <Box sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid #E5E7EB'
            }} data-id="element-1200">
                <Typography sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: '#6B7280',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }} data-id="element-1201">
                  Currency Balances
                </Typography>
              </Box>
              <TableContainer data-id="element-1202">
                <Table data-id="element-1203">
                  <TableHead data-id="element-1204">
                    <TableRow sx={{
                    bgcolor: '#F3F5F7'
                  }} data-id="element-1205">
                      <TableCell sx={{
                      fontWeight: 600,
                      color: '#0052CC',
                      py: 2
                    }} data-id="element-1206">
                        Currency
                      </TableCell>
                      <TableCell sx={{
                      fontWeight: 600,
                      color: '#0052CC',
                      py: 2
                    }} data-id="element-1207">
                        Available
                      </TableCell>
                      <TableCell sx={{
                      fontWeight: 600,
                      color: '#0052CC',
                      py: 2
                    }} data-id="element-1208">
                        Reserved
                      </TableCell>
                      <TableCell sx={{
                      fontWeight: 600,
                      color: '#0052CC',
                      py: 2
                    }} data-id="element-1209">
                        Total
                      </TableCell>
                      <TableCell sx={{
                      fontWeight: 600,
                      color: '#0052CC',
                      py: 2
                    }} data-id="element-1210">
                        Threshold
                      </TableCell>
                      <TableCell sx={{
                      fontWeight: 600,
                      color: '#0052CC',
                      py: 2
                    }} data-id="element-1211">
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody data-id="element-1212">
                    {currencyBalances.map(row => <TableRow key={row.currency} sx={{
                    '&:hover': {
                      bgcolor: '#F9FAFB'
                    },
                    '& td': {
                      py: 2,
                      borderBottom: '1px solid #F3F5F7'
                    }
                  }} data-id="element-1213">
                        <TableCell data-id="element-1214">
                          <Box data-id="element-1215">
                            <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: '#1F2937'
                        }} data-id="element-1216">
                              {row.currency}
                            </Typography>
                            <Typography variant="caption" sx={{
                          color: '#9CA3AF'
                        }} data-id="element-1217">
                              {row.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{
                      fontWeight: 500,
                      color: '#0B7923'
                    }} data-id="element-1218">
                          {row.available}
                        </TableCell>
                        <TableCell sx={{
                      color: '#6B7280'
                    }} data-id="element-1219">
                          {row.reserved}
                        </TableCell>
                        <TableCell sx={{
                      fontWeight: 500,
                      color: '#1F2937'
                    }} data-id="element-1220">
                          {row.total}
                        </TableCell>
                        <TableCell sx={{
                      color: '#6B7280'
                    }} data-id="element-1221">
                          {row.threshold}
                        </TableCell>
                        <TableCell data-id="element-1222">
                          <Chip label={row.status} size="small" sx={{
                        bgcolor: getStatusColor(row.status).bg,
                        color: getStatusColor(row.status).text,
                        fontWeight: 600,
                        fontSize: '11px',
                        height: '22px',
                        borderRadius: '11px'
                      }} data-id="element-1223" />
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Chart */}
            <Paper elevation={0} sx={{
            borderRadius: 2,
            border: '1px solid #E5E7EB',
            p: 4
          }} data-id="element-1224">
              <Typography sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#6B7280',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              mb: 4
            }} data-id="element-1225">
                30-Day Balance History (NGN Millions)
              </Typography>
              <Box sx={{
              height: 320,
              width: '100%'
            }} data-id="element-1226">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1227">
                  <LineChart data={chartData} margin={{
                  top: 10,
                  right: 30,
                  bottom: 10,
                  left: 10
                }} data-id="element-1228">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1229" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                    fill: '#6B7280',
                    fontSize: 12
                  }} data-id="element-1230" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                    fill: '#6B7280',
                    fontSize: 12
                  }} unit="M" data-id="element-1231" />
                    <Tooltip contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0px 4px 12px rgba(0,0,0,0.08)'
                  }} data-id="element-1232" />
                    <ReferenceLine y={5} stroke="#AE2A19" strokeDasharray="6 4" label={{
                    position: 'insideTopLeft',
                    value: 'Min. Threshold',
                    fill: '#AE2A19',
                    fontSize: 12,
                    fontWeight: 600
                  }} data-id="element-1233" />
                    <Line type="monotone" dataKey="balance" stroke="#0052CC" strokeWidth={2.5} dot={{
                    r: 4,
                    fill: '#0052CC',
                    strokeWidth: 0
                  }} activeDot={{
                    r: 6
                  }} data-id="element-1234" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Bottom Row for Settings and Top-Up */}
        <Grid item xs={12} md={6} data-id="element-1235">
          {/* Alert Settings */}
          <Paper elevation={0} sx={{
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          height: '100%'
        }} data-id="element-1236">
            <Box sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1237">
              <BellIcon size={18} color="#6B7280" data-id="element-1238" />
              <Typography sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#6B7280',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }} data-id="element-1239">
                Alert Settings
              </Typography>
            </Box>
            <Box sx={{
            p: 3
          }} data-id="element-1240">
              <TextField fullWidth label="Global Threshold (NGN)" defaultValue="2000000" type="number" size="small" sx={{
              mb: 3
            }} data-id="element-1241" />
              <Typography variant="subtitle2" sx={{
              fontWeight: 600,
              mb: 1.5,
              color: '#374151',
              fontSize: '13px'
            }} data-id="element-1242">
                Notify me via:
              </Typography>
              <FormControlLabel control={<Switch defaultChecked color="primary" size="small" data-id="element-1244" />} label={<Typography variant="body2" data-id="element-1245">Email</Typography>} sx={{
              display: 'block',
              mb: 0.5
            }} data-id="element-1243" />
              <FormControlLabel control={<Switch defaultChecked color="primary" size="small" data-id="element-1247" />} label={<Typography variant="body2" data-id="element-1248">SMS</Typography>} sx={{
              display: 'block'
            }} data-id="element-1246" />
              <Button variant="outlined" fullWidth size="small" sx={{
              mt: 3,
              color: '#0052CC',
              borderColor: '#E5E7EB'
            }} data-id="element-1249">
                Save Settings
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} data-id="element-1250">
          {/* Top-Up Request */}
          <Paper elevation={0} sx={{
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          height: '100%'
        }} data-id="element-1251">
            <Box sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1252">
              <PlusCircleIcon size={18} color="#6B7280" data-id="element-1253" />
              <Typography sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: '#6B7280',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }} data-id="element-1254">
                Top-Up Request
              </Typography>
            </Box>
            <Box sx={{
            p: 3
          }} data-id="element-1255">
              <Grid container spacing={2} data-id="element-1256">
                <Grid item xs={8} data-id="element-1257">
                  <TextField fullWidth label="Amount" type="number" size="small" data-id="element-1258" />
                </Grid>
                <Grid item xs={4} data-id="element-1259">
                  <TextField fullWidth select label="Currency" defaultValue="NGN" size="small" data-id="element-1260">
                    <MenuItem value="NGN" data-id="element-1261">NGN</MenuItem>
                    <MenuItem value="GHS" data-id="element-1262">GHS</MenuItem>
                    <MenuItem value="KES" data-id="element-1263">KES</MenuItem>
                    <MenuItem value="XOF" data-id="element-1264">XOF</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} data-id="element-1265">
                  <TextField fullWidth label="Bank Reference / Notes" multiline rows={2} size="small" data-id="element-1266" />
                </Grid>
                <Grid item xs={12} data-id="element-1267">
                  <Button variant="contained" fullWidth sx={{
                  bgcolor: '#0052CC',
                  '&:hover': {
                    bgcolor: '#003DA8'
                  }
                }} data-id="element-1268">
                    Submit Request
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{
              my: 3
            }} data-id="element-1269" />

              <Button variant="text" fullWidth onClick={() => setShowHistory(!showHistory)} endIcon={showHistory ? <ChevronUpIcon size={16} data-id="element-1271" /> : <ChevronDownIcon size={16} data-id="element-1272" />} sx={{
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '13px',
              justifyContent: 'flex-start',
              px: 0
            }} data-id="element-1270">
                View History
              </Button>

              <Collapse in={showHistory} data-id="element-1273">
                <Box sx={{
                mt: 2
              }} data-id="element-1274">
                  {previousTopUps.map((item, i) => <Box key={i} sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1.5,
                  p: 1.5,
                  bgcolor: '#F9FAFB',
                  borderRadius: 1
                }} data-id="element-1275">
                      <Box data-id="element-1276">
                        <Typography variant="body2" sx={{
                      fontWeight: 600,
                      color: '#1F2937',
                      fontSize: '13px'
                    }} data-id="element-1277">
                          {item.amount}
                        </Typography>
                        <Typography variant="caption" sx={{
                      color: '#9CA3AF'
                    }} data-id="element-1278">
                          {item.date}
                        </Typography>
                      </Box>
                      <Chip label={item.status} size="small" sx={{
                    bgcolor: '#ECFDF5',
                    color: '#0B7923',
                    fontWeight: 600,
                    fontSize: '10px',
                    height: '20px',
                    borderRadius: '10px'
                  }} data-id="element-1279" />
                    </Box>)}
                </Box>
              </Collapse>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>;
};
export default FloatBalancePage;